import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { useForm } from "react-hook-form";
import PageHeader from "../../components/common/PageHeader";
import Spinner from "../../components/common/Spinner";
import ErrorAlert from "../../components/common/ErrorAlert";
import { Contact, ContactType } from "@akfdev-com/shared/src/models/Contact";
import { Address, formatAddress } from "@akfdev-com/shared/src/models";
import { parseLegacyAddress } from "@akfdev-com/shared/src/geocoding/parseLegacyAddress";
import { AddressForm, PartialAddress } from "../../components/ui";
import { contactsApi } from "../../services/api";

interface ContactFormData {
  name: string;
  type: ContactType;
  email: string;
  phone: string;
  notes: string;
}

/**
 * Contact Edit Page
 *
 * Same module layout as ContactDetails. When the type selector changes,
 * recompute modules within 1 second without full page reload.
 * Type selector disabled on existing records (immutable after creation).
 *
 * Validates: Requirements 5.6, 6.1, 6.2, 6.3, 6.6
 */
const ContactEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [contact, setContact] = useState<Contact | null>(null);
  const [loading, setLoading] = useState(true);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showResidentConfirm, setShowResidentConfirm] = useState(false);
  const [converting, setConverting] = useState(false);
  const [pendingFormData, setPendingFormData] =
    useState<ContactFormData | null>(null);
  // Structured Address state (address-geolocation Rule 21). Preloaded from
  // the stored contact when present. Legacy `Contact.address` string is
  // preserved on the record until the user saves a new structured value.
  const [structuredAddress, setStructuredAddress] = useState<PartialAddress>(
    {},
  );
  const [legacyAddress, setLegacyAddress] = useState<string>("");
  // Auto-service-charge toggle (Bucket C #9). Only meaningful when the
  // contact's type is 'resident' — surfaced conditionally in the UI.
  // On import, an eligible invoice (unit + single active plan resolved)
  // is written as `type: 'service_charge'` with a balanced GL demand
  // posting when this is true.
  const [autoServiceChargeOnImport, setAutoServiceChargeOnImport] =
    useState<boolean>(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ContactFormData>();

  useEffect(() => {
    const fetchContact = async () => {
      if (!id) return;
      try {
        const data = await contactsApi.get(id);
        setContact(data);
        reset({
          name: data.name,
          type: data.type,
          email: data.email ?? "",
          phone: data.phone ?? "",
          notes: data.notes ?? "",
        });
        // Pre-fill the structured fields either from the stored
        // `structuredAddress` (when P2 adoption already migrated the
        // record) or from a best-effort parse of the legacy string
        // (when the record predates adoption). The parser is a pure
        // heuristic — the user always sees the result before saving
        // and can correct anything that mis-mapped.
        const initialStructured: PartialAddress =
          data.structuredAddress ??
          (data.address ? parseLegacyAddress(data.address) : {});
        setStructuredAddress(initialStructured);
        setLegacyAddress(data.address ?? "");
        setAutoServiceChargeOnImport(Boolean(data.autoServiceChargeOnImport));
      } catch {
        setSaveError("Failed to load contact. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchContact();
  }, [id, reset]);

  const onSubmit = async (data: ContactFormData) => {
    if (!id) return;

    // If type is changing to resident, ask for confirmation first
    if (data.type === "resident" && contact?.type !== "resident") {
      setPendingFormData(data);
      setShowResidentConfirm(true);
      return;
    }

    await saveContact(data);
  };

  const saveContact = async (data: ContactFormData) => {
    if (!id) return;
    try {
      setSaveError(null);
      // Compose the structured Address payload + regenerate the legacy
      // `address` string via formatAddress (address-geolocation Req 11.1,
      // 11.2). Only send `structuredAddress` when the user has entered at
      // least one field — an empty AddressForm leaves the record's stored
      // structured field untouched.
      const hasAnyStructured =
        !!structuredAddress.addressLine1 ||
        !!structuredAddress.town ||
        !!structuredAddress.postcode ||
        !!structuredAddress.countryCode;
      const asAddress: Address = {
        organisation: structuredAddress.organisation,
        addressLine1: structuredAddress.addressLine1 ?? "",
        addressLine2: structuredAddress.addressLine2,
        locality: structuredAddress.locality,
        town: structuredAddress.town ?? "",
        county: structuredAddress.county,
        postcode: structuredAddress.postcode ?? "",
        countryCode: structuredAddress.countryCode ?? "",
      };
      const nextLegacyAddress = hasAnyStructured
        ? formatAddress(asAddress)
        : legacyAddress; // preserve pre-existing legacy string when the form is empty
      const payload: any = {
        ...data,
        address: nextLegacyAddress,
        ...(hasAnyStructured ? { structuredAddress: asAddress } : {}),
        autoServiceChargeOnImport,
      };
      await contactsApi.update(id, payload);
      navigate(`/dashboard/contacts/${id}`);
    } catch {
      setSaveError("Failed to save changes. Please try again.");
    }
  };

  const handleConfirmConvert = async () => {
    if (!id || !pendingFormData) return;
    try {
      setConverting(true);
      setSaveError(null);
      // Convert to resident — updates contact, invoices, and payments
      await contactsApi.convertToResident(id);
      // Save remaining form fields (name, email, etc.)
      const { type: _type, ...otherFields } = pendingFormData;
      if (Object.values(otherFields).some((v) => v !== "")) {
        await contactsApi.update(id, otherFields);
      }
      navigate(`/dashboard/contacts/${id}`);
    } catch {
      setSaveError("Failed to convert contact to resident. Please try again.");
    } finally {
      setConverting(false);
      setShowResidentConfirm(false);
      setPendingFormData(null);
    }
  };

  const handleCancelConvert = () => {
    setShowResidentConfirm(false);
    setPendingFormData(null);
  };

  if (loading) return <Spinner message="Loading contact..." />;
  if (!contact && saveError) return <ErrorAlert message={saveError} />;

  return (
    <div>
      {/* Breadcrumb */}
      <div className="mb-4 flex items-center gap-2 text-sm text-gray-500">
        <Link
          to="/dashboard/contacts"
          className="hover:text-primary-600 transition-colors"
        >
          Contacts
        </Link>
        <span>/</span>
        <Link
          to={`/dashboard/contacts/${id}`}
          className="hover:text-primary-600 transition-colors"
        >
          {contact?.name}
        </Link>
        <span>/</span>
        <span className="text-gray-900 font-medium">Edit</span>
      </div>

      <PageHeader
        title={`Edit — ${contact?.name}`}
        description="Update contact details below."
      />

      {saveError && (
        <div className="mt-4">
          <ErrorAlert message={saveError} />
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="mt-6">
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-5 border-b border-gray-200">
            <h2 className="text-base font-semibold text-gray-900">
              Contact Information
            </h2>
          </div>

          <div className="px-6 py-6 space-y-6">
            {/* Name */}
            <div>
              <label htmlFor="name" className="form-label">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                id="name"
                type="text"
                className={`form-input mt-1 ${
                  errors.name
                    ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                    : ""
                }`}
                placeholder="Full name or company name"
                {...register("name", { required: "Name is required" })}
              />
              {errors.name && (
                <p className="mt-1 text-xs text-red-600">
                  {errors.name.message}
                </p>
              )}
            </div>

            {/* Type */}
            <div>
              <label htmlFor="type" className="form-label">
                Type <span className="text-red-500">*</span>
              </label>
              <select
                id="type"
                className="form-input mt-1"
                {...register("type", { required: true })}
              >
                <option value="resident">Resident</option>
                <option value="supplier">Supplier</option>
                <option value="customer">Customer</option>
              </select>
            </div>

            {/* Auto-reclassify future invoices as service charges — Bucket
                C #9. Auto-set the first time a contact is classified as
                resident at import; toggleable here for existing contacts
                and to opt-out of auto-reclassification. */}
            <div>
              <div className="flex items-center gap-2">
                <input
                  id="autoServiceChargeOnImport"
                  type="checkbox"
                  checked={autoServiceChargeOnImport}
                  onChange={(e) =>
                    setAutoServiceChargeOnImport(e.target.checked)
                  }
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label
                  htmlFor="autoServiceChargeOnImport"
                  className="text-sm font-medium text-gray-700"
                >
                  Auto-classify future invoices as service charges
                </label>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                When on, every FreeAgent invoice raised against this contact on
                a future import is persisted as a service-charge demand (with
                the resident's unit and the building's active plan) and posts{" "}
                <code>Dr 1910 / Cr 1001</code>. Requires the contact to be
                linked to a specific unit and the building to have exactly one
                active service-charge plan; otherwise the invoice lands as a
                plain invoice and appears in the import summary for manual
                reclassification.
              </p>
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="form-label">
                Email
              </label>
              <input
                id="email"
                type="email"
                className={`form-input mt-1 ${
                  errors.email
                    ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                    : ""
                }`}
                placeholder="email@example.com"
                {...register("email", {
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: "Enter a valid email address",
                  },
                })}
              />
              {errors.email && (
                <p className="mt-1 text-xs text-red-600">
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* Phone */}
            <div>
              <label htmlFor="phone" className="form-label">
                Phone
              </label>
              <input
                id="phone"
                type="tel"
                className="form-input mt-1"
                placeholder="+44 7700 900000"
                {...register("phone")}
              />
            </div>

            {/* Structured Address (address-geolocation Rule 21) */}
            <div>
              <label className="form-label mb-2 block">Address</label>
              {legacyAddress && !contact?.structuredAddress ? (
                <p className="text-xs text-gray-500 mb-2 bg-yellow-50 border border-yellow-200 rounded px-2 py-1">
                  Auto-parsed from legacy address:{" "}
                  <span className="font-medium">{legacyAddress}</span>
                  <br />
                  Review the fields below and correct anything that mis-mapped.
                  The legacy string will be regenerated on save.
                </p>
              ) : null}
              <AddressForm
                value={structuredAddress}
                onChange={setStructuredAddress}
                idPrefix="contact-edit"
              />
            </div>

            {/* Notes */}
            <div>
              <label htmlFor="notes" className="form-label">
                Notes
              </label>
              <textarea
                id="notes"
                rows={4}
                className="form-input mt-1 resize-none"
                placeholder="Any additional notes..."
                {...register("notes")}
              />
            </div>
          </div>

          <div className="px-4 sm:px-6 py-4 bg-gray-50 border-t border-gray-200 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between rounded-b-lg">
            <Link
              to={`/dashboard/contacts/${id}`}
              className="inline-flex items-center justify-center w-full sm:w-auto gap-1.5 text-base sm:text-sm text-gray-600 hover:text-gray-900 transition-colors border border-gray-300 rounded-md px-4 py-2.5 sm:py-2 bg-white hover:bg-gray-50 min-h-[44px] sm:min-h-0"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              Cancel
            </Link>
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn btn-primary w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      </form>

      {/* Confirmation dialog for converting to resident */}
      {showResidentConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Convert to Resident?
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Changing this contact to a resident will automatically convert all
              their invoices to <strong>Service Charge</strong> and update their
              linked payments. This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handleCancelConvert}
                disabled={converting}
                className="btn btn-secondary text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmConvert}
                disabled={converting}
                className="btn btn-primary text-sm disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {converting ? "Converting..." : "Yes, Convert"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactEdit;
